import React, { useEffect, useState } from 'lib/react';
import VirtualizedTable from 'commonComponents/virtualizedTable';
import RetryScreen from 'commonComponents/retry-screen';
import {
  EVButton, EVTypography, EVGrid, EVDownloadIcon, EVPaper,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import { useDispatch, useSelector } from 'lib/redux';
import { camelCaseToWords } from 'utils/app.utils';
import {
  getReportAnalyticsCells,
  getRequestParamsForReportAnalytics,
} from 'features/analyticsDashboard/AnalyticsDashboard.utils';
import Typography from 'commonComponents/typography';
import useMediaQuery from 'hooks/useMediaQuery';
import useAbortController from 'hooks/useAbortController';
import {
  NOTIFICATION_START, NOTIFICATION_SUCCESS, NOTIFICATION_ERROR, NOTIFICATION_CLOSE,
} from 'constants/ActionTypes';
import { CLIENT_ERROR_RESPONSES } from 'constants/AppConstants';
import downloadReportAnalyticsCsvService from 'services/downloadReportAnalyticsCsv.service';
import ExportDataLoader from './ExportDataLoader.component';
import ExportDataStyles from './ExportDataStyles';
import { fetchReportAnalytics as fetchReportAnalyticsAction } from '../../AnalyticsDashboard.actions';
import PageOptions from './PageOptions.component';
import ExportDataList from './ExportDataList.component';

// #genai: helper extracted from a nested ternary (Sonar S3358); preserves original evaluation order/behavior
const getNotificationType = (response) => {
  if (response.ok) return NOTIFICATION_SUCCESS;
  if (response.status === CLIENT_ERROR_RESPONSES.STATUS_ABORT_REQUEST) return NOTIFICATION_CLOSE;
  return NOTIFICATION_ERROR;
};

function ExportData() {
  const classes = ExportDataStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(640);
  const isTablet = useMediaQuery(1024);

  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const {
    reportAnalytics,
    loading,
    error,
    reportsCount,
    appliedReportAnalyticsFilters,
    productsList,
  } = useSelector(({ analyticsDashboard }) => ({
    reportAnalytics: analyticsDashboard.reportAnalytics,
    loading: analyticsDashboard.loading.reportAnalytics,
    error: analyticsDashboard.error.reportAnalytics,
    reportsCount: analyticsDashboard.reportsCount,
    appliedReportAnalyticsFilters: analyticsDashboard.appliedReportAnalyticsFilters,
    productsList: analyticsDashboard.productsList,
  }));

  const orgId = useSelector(({ userData }) => userData.initParameters?.orgNodeId);
  const { getController } = useAbortController();
  const fetchReportAnalytics = (refreshData = false) => dispatch(fetchReportAnalyticsAction({ refreshData }));
  useEffect(() => fetchReportAnalytics(), []);

  const cellProps = { className: classes.tableCell };

  const columns = Object.keys(reportAnalytics[0] || {}).map((key) => ({ label: camelCaseToWords(key), dataKey: key, cellProps }));
  const reportAnalyticsList = getReportAnalyticsCells(reportAnalytics);

  const handleDownloadExcel = () => {
    // #genai: keep analytics export CSV request path after merge conflict resolution.
    const controller = getController();
    const { signal } = controller;
    const totalReports = reportsCount;
    setDownloadingCsv(true);

    const requestParams = getRequestParamsForReportAnalytics(
      appliedReportAnalyticsFilters,
      productsList,
    );
    dispatch({
      type: NOTIFICATION_START,
      title: t('notification_bar__download_excel_progress_title', { totalReports }),
      footer: t('notification_bar__download_excel_progress_footer'),
    });

    downloadReportAnalyticsCsvService(orgId, requestParams, signal)
      .then((response) => {
        // #genai: extract nested ternary into a helper function (Sonar S3358); evaluation order/behavior unchanged
        const notificationType = getNotificationType(response);
        const title = response.ok ? t('notification_bar__download_excel_success') : t('notification_bar__download_excel_error');

        dispatch({
          type: notificationType,
          title,
          footer: '',
        });
        setDownloadingCsv(false);
      })
      .catch(() => {
        dispatch({
          type: NOTIFICATION_ERROR,
          title: t('notification_bar__download_excel_error'),
          footer: '',
        });
        setDownloadingCsv(false);
      });
  };

  if (loading) return <ExportDataLoader />;

  if (error) return <RetryScreen handleRetryButtonClick={() => fetchReportAnalytics(true)} style={{ boxShadow: 'none' }} />;

  return (
    <EVGrid className={classes.reportAnalyticsMainContainer}>
      <EVPaper elevation={0} className={classes.reportAnalytics}>
        <EVGrid className={classes.downloadExcelButtonContainer}>
          <Typography variant="h3-600">Reports</Typography>
          <EVButton
            id="download-excel-btn"
            variant="contained"
            color="primary"
            onClick={handleDownloadExcel}
            className={classes.downloadExcelButton}
            disabled={downloadingCsv || loading}
          >
            <EVDownloadIcon id="download-excel-icon" />
            <EVTypography className={classes.downloadExcelText}>{t('order_history__download_Excel')}</EVTypography>
          </EVButton>
        </EVGrid>
        {(isMobile || isTablet) ? (
          <ExportDataList
            loading={loading}
            reportAnalyticsList={reportAnalyticsList}
          />
        )
          : (
            <EVGrid className={classes.tableContainer}>
              <VirtualizedTable
                columns={columns}
                dataList={reportAnalyticsList}
                rowKey="reportId"
                headerProps={{ className: classes.tableHeader }}
                bodyProps={{ className: classes.tableBody }}
                className={classes.table}
              />
            </EVGrid>
          )}
      </EVPaper>
      <EVPaper elevation={0}>
        <PageOptions loading={loading} />
      </EVPaper>
    </EVGrid>
  );
}

export default ExportData;
